import { TRANSACTION_TYPE_OPTIONS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils';
import {
	BULK_EDIT_TRANSACTIONS_FORM_FIELD,
	BulkUpdateTransactionsFormValues,
	useBulkUpdateTransactionsDrawerController,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/BulkUpdateTransactionsDrawer/useBulkUpdateTransactionsDrawerController';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { Button, Drawer, Text } from '@mantine/core';
import { memo, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Select } from 'react-hook-form-mantine';
import styled from 'styled-components';

import { EntitySelect } from './EntitySelect/EntitySelect';
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@dealstack/frontend/shared/components/Alert/Alert.tsx';
import { MantineDateInput } from '@dealstack/frontend/shared/components/mantineForm/MantineDateInput.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';

interface Props {
	className?: string;
	opened: boolean;
	onClose: () => void;
	updateField: BULK_EDIT_TRANSACTIONS_FORM_FIELD;
	onSubmit: (formValues: BulkUpdateTransactionsFormValues) => void;
	transactionTotalCount?: number;
	selectedCount?: number;
	isSubmitting?: boolean;
}

const BulkUpdateTransactionsDrawer = ({
	opened,
	onClose,
	updateField,
	onSubmit,
	transactionTotalCount,
	selectedCount,
	isSubmitting,
	className,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const { editTransactionForm, mipSelectOptions } = useBulkUpdateTransactionsDrawerController();
	const [disableUpdateButton, setDisableUpdateButton] = useState<boolean>(false);

	useEffect(() => {
		if (!opened) {
			editTransactionForm.reset();
		}
	}, [opened]);

	const title = (() => {
		switch (updateField) {
			case BULK_EDIT_TRANSACTIONS_FORM_FIELD.TYPE:
				return tCapManage('transaction.bulk_update_drawer.type_title');
			case BULK_EDIT_TRANSACTIONS_FORM_FIELD.BUYER:
				return tCapManage('transaction.bulk_update_drawer.buyer_title');
			case BULK_EDIT_TRANSACTIONS_FORM_FIELD.SELLER:
				return tCapManage('transaction.bulk_update_drawer.seller_title');
			case BULK_EDIT_TRANSACTIONS_FORM_FIELD.BUYER_MIP:
				return tCapManage('transaction.bulk_update_drawer.buyer_mip_title');
			case BULK_EDIT_TRANSACTIONS_FORM_FIELD.SELLER_MIP:
				return tCapManage('transaction.bulk_update_drawer.seller_mip_title');
			case BULK_EDIT_TRANSACTIONS_FORM_FIELD.DATE:
				return tCapManage('transaction.bulk_update_drawer.date_title');
			default:
				return '';
		}
	})();

	const WarningAlert = ({ description, title }: { description: string; title?: string }) => (
		<Alert icon="warning-filled" state="warning" size="s">
			{title ? <AlertTitle>{title}</AlertTitle> : null}
			<AlertDescription>{description}</AlertDescription>
		</Alert>
	);

	return (
		<Drawer
			opened={opened}
			onClose={() => {
				onClose();
				editTransactionForm.reset();
			}}
			position="right"
			className={className}
			transitionProps={drawerTransitionProps}
			title={<Text size="B4Bold">{title}</Text>}
		>
			<FormProvider {...editTransactionForm}>
				<form onSubmit={editTransactionForm.handleSubmit(onSubmit)}>
					<Text size="B5Medium" c="blue.6" mb="1rem">
						{tCapManage('transactions.bulk_update_drawer.selected_of_all_count', {
							selectedCount,
							transactionTotalCount,
						})}
					</Text>

					{updateField === BULK_EDIT_TRANSACTIONS_FORM_FIELD.TYPE && (
						<Select
							name={BULK_EDIT_TRANSACTIONS_FORM_FIELD.TYPE}
							control={editTransactionForm.control}
							data={TRANSACTION_TYPE_OPTIONS}
							data-testid={TEST_ELEMENT_IDS.CAP_TRANSACTION_BULK_UPDATE_TYPE_SELECT}
							label={tCapManage('transactions.drawer.transaction_type.label')}
							placeholder={tCapManage(
								'transactions.drawer.transaction_type.placeholder'
							)}
							size="md"
							clearable
							allowDeselect
						/>
					)}

					{updateField === BULK_EDIT_TRANSACTIONS_FORM_FIELD.BUYER && (
						<EntitySelect
							name={BULK_EDIT_TRANSACTIONS_FORM_FIELD.BUYER}
							dataTestId={TEST_ELEMENT_IDS.CAP_TRANSACTION_BULK_UPDATE_BUYER_SELECT}
							control={editTransactionForm.control}
							label={tCapManage('transactions.drawer.buyer.label')}
							placeholder={tCapManage('transactions.drawer.buyer.placeholder')}
							setDisableButton={setDisableUpdateButton}
						/>
					)}

					{updateField === BULK_EDIT_TRANSACTIONS_FORM_FIELD.SELLER && (
						<EntitySelect
							name={BULK_EDIT_TRANSACTIONS_FORM_FIELD.SELLER}
							dataTestId={TEST_ELEMENT_IDS.CAP_TRANSACTION_BULK_UPDATE_SELLER_SELECT}
							control={editTransactionForm.control}
							label={tCapManage('transactions.drawer.seller.label')}
							placeholder={tCapManage('transactions.drawer.seller.placeholder')}
							setDisableButton={setDisableUpdateButton}
						/>
					)}

					{updateField === BULK_EDIT_TRANSACTIONS_FORM_FIELD.BUYER_MIP && (
						<>
							<p>{tCapManage('transactions.drawer.holding.info')}</p>
							<WarningAlert
								title={tCapManage('transactions.drawer.buyer_mip.alert.title')}
								description={tCapManage(
									'transactions.drawer.buyer_mip.alert.description'
								)}
							/>
							<Select
								name={BULK_EDIT_TRANSACTIONS_FORM_FIELD.BUYER_MIP}
								control={editTransactionForm.control}
								data-testid={
									TEST_ELEMENT_IDS.CAP_TRANSACTION_BULK_UPDATE_BUYER_MIP_SELECT
								}
								label={tCapManage('transactions.drawer.buyer_mip.label')}
								placeholder={tCapManage('transactions.drawer.buyer_mip.label')}
								data={mipSelectOptions}
								size="md"
							/>
						</>
					)}

					{updateField === BULK_EDIT_TRANSACTIONS_FORM_FIELD.SELLER_MIP && (
						<>
							<p>{tCapManage('transactions.drawer.holding.info')}</p>
							<WarningAlert
								title={tCapManage('transactions.drawer.seller_mip.alert.title')}
								description={tCapManage(
									'transactions.drawer.seller_mip.alert.description'
								)}
							/>
							<Select
								name={BULK_EDIT_TRANSACTIONS_FORM_FIELD.SELLER_MIP}
								control={editTransactionForm.control}
								label={tCapManage('transactions.drawer.seller_holding.label')}
								placeholder={tCapManage(
									'transactions.drawer.seller_holding.placeholder'
								)}
								data={mipSelectOptions}
								size="md"
							/>
						</>
					)}

					{updateField === BULK_EDIT_TRANSACTIONS_FORM_FIELD.DATE && (
						<MantineDateInput
							name={BULK_EDIT_TRANSACTIONS_FORM_FIELD.DATE}
							maxDate={new Date()}
							label={tCapManage('transactions.drawer.date.label')}
							size="md"
						/>
					)}

					<Button
						type="submit"
						fullWidth
						mt="auto"
						disabled={disableUpdateButton || isSubmitting}
						loading={isSubmitting}
						data-testid={TEST_ELEMENT_IDS.CAP_TRANSACTION_BULK_UPDATE_SUBMIT}
					>
						{tCapManage('transactions.bulk_update_drawer.update_button')}
					</Button>
				</form>
			</FormProvider>
		</Drawer>
	);
};

const Memoized = memo(BulkUpdateTransactionsDrawer);
const Styled = styled(Memoized)`
	.mantine-Drawer-header {
		padding-bottom: 0.5rem;
	}

	form {
		height: 100%;
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}
`;

export { Styled as BulkUpdateTransactionsDrawer };
